
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import type { ApiError } from '../api/client';
import { adminApi } from '../api/endpoints';
import type {
  AdminUser,
  AdminUserCreateRequest,
  AdminUserCredentials,
  AdminUserUpdateRequest,
  Id,
} from '../types/api';
import { queryKeys } from './keys';

export function useAdminUsers(enabled = true): UseQueryResult<AdminUser[], ApiError> {
  return useQuery<AdminUser[], ApiError>({
    queryKey: queryKeys.adminUsers,
    queryFn: () => adminApi.listUsers(),
    enabled,
    staleTime: 30_000,
    retry: (failureCount, error) => !error.isNotFound && !error.isUnauthorized && failureCount < 2,
  });
}

export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation<AdminUserCredentials, ApiError, AdminUserCreateRequest>({
    mutationFn: (payload) => adminApi.createUser(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminUsers }),
  });
}

export function useResetAdminUserPassword() {
  const qc = useQueryClient();
  return useMutation<AdminUserCredentials, ApiError, Id>({
    mutationFn: (userId) => adminApi.resetPassword(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminUsers }),
  });
}

export function useUpdateAdminUser() {
  const qc = useQueryClient();
  return useMutation<AdminUser, ApiError, { userId: Id; body: AdminUserUpdateRequest }>({
    mutationFn: ({ userId, body }) => adminApi.updateUser(userId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminUsers }),
  });
}

export function useAdminTeams(enabled = true): UseQueryResult<string[], ApiError> {
  return useQuery<string[], ApiError>({
    queryKey: queryKeys.adminTeams,
    queryFn: () => adminApi.teams(),
    enabled,
    staleTime: 60_000,
  });
}
